import { useEffect, useMemo, useState } from "react";

import ElementaryProgressReportDao from "../dao/ElementaryProgressReportDao";

// Utility function to check if homeroom group has EDIT permission for current user
const checkHomeroomGroupEditPermission = (homeroomGroup, gradingCriteriaId, userGuid) => {
    if (!userGuid) return false;

    // Find the share specific to the current user
    const userShare = homeroomGroup.homeroomGroupShares?.find((share) => Number(share.guid) === Number(userGuid));

    if (!userShare) return false;

    // Check if this user has EDIT permission for this specific grading criteria
    return userShare.gradingCriteriaShares?.some(
        (criteriaShare) =>
            Number(criteriaShare.gradingCriteriaId) === Number(gradingCriteriaId) && criteriaShare.permission === "EDIT"
    );
};

// Utility function to find grading criteria in a profile
const findGradingCriteria = (gradingCriteriaList, selectedTemplateId) => {
    return gradingCriteriaList?.find((criteria) => Number.parseInt(criteria.parentTemplate, 10) === selectedTemplateId);
};

// Utility function to get matching shared group info
const getSharedGroupInfo = (homeroomGroup, allSharedGroupOptions, sharedProfile) => {
    const matchingSharedGroup = allSharedGroupOptions?.find(
        (g) => Number(g.sharedMeta?.sharedGroupId) === Number(homeroomGroup.key)
    );

    return {
        teacherName:
            matchingSharedGroup?.sharedMeta?.sharedGroupCreatorDisplayName || sharedProfile.ownerName || "Unknown"
    };
};

// Utility function to process all shared profiles
const processAllSharedProfiles = (
    sharedGradingProfileDtos,
    selectedTemplate,
    accessibleSharedGroupOptions,
    selectedGrade,
    userDetails
) => {
    const students = [];

    sharedGradingProfileDtos.forEach((sharedProfile) => {
        const gradingCriteria = findGradingCriteria(sharedProfile.gradingCriteriaList, selectedTemplate.templateId);

        if (gradingCriteria) {
            const groupStudents = processSharedProfileGroups(
                sharedProfile,
                gradingCriteria,
                accessibleSharedGroupOptions,
                selectedGrade,
                userDetails
            );
            students.push(...groupStudents);
        }
    });

    return students;
};

// Utility function to get homeroom group student IDs (normalized to strings for consistent Set comparison)
const getHomeroomGroupStudentIds = (homeroomGroup) => {
    return new Set((homeroomGroup.homeroomGroupStudents || []).map((s) => String(s.studentNumber)));
};

// Utility function to filter and transform shared students
const filterAndTransformSharedStudents = (sharedProfile, homeroomGroup, teacherName, selectedGrade, studentIds) => {
    return (sharedProfile.gradingProfileStudents || [])
        .filter((student) => studentIds.has(String(student.studentNumber)))
        .map((student) => transformSharedStudent(student, sharedProfile, homeroomGroup, teacherName, selectedGrade));
};

// Utility function to process all homeroom groups in a shared profile
const processSharedProfileGroups = (
    sharedProfile,
    gradingCriteria,
    accessibleSharedGroupOptions,
    selectedGrade,
    userDetails
) => {
    const students = [];
    const homeroomGroups = sharedProfile.homeroomGroups || [];

    homeroomGroups.forEach((homeroomGroup) => {
        // Check if this specific group has EDIT permission for the grading criteria
        if (!checkHomeroomGroupEditPermission(homeroomGroup, gradingCriteria.key, userDetails?.uid)) return;

        // Verify this group is in accessibleSharedGroupOptions (teacher has access to it)
        const isGroupAccessible = accessibleSharedGroupOptions?.some(
            (g) => Number(g.sharedMeta?.sharedGroupId) === Number(homeroomGroup.key)
        );
        if (!isGroupAccessible) return;

        const { teacherName } = getSharedGroupInfo(homeroomGroup, accessibleSharedGroupOptions, sharedProfile);
        const studentIds = getHomeroomGroupStudentIds(homeroomGroup);
        const groupStudents = filterAndTransformSharedStudents(
            sharedProfile,
            homeroomGroup,
            teacherName,
            selectedGrade,
            studentIds
        );

        students.push(...groupStudents);
    });

    return students;
};

// Utility function to transform student with homeroom info
const transformHomeroomStudent = (student, homeroomProfile) => {
    return {
        ...student,
        source: "homeroom",
        sourceProfile: homeroomProfile,
        homeroomTeacher: "My Homeroom",
        homeroomGrade: homeroomProfile.gradeName
    };
};

// Utility function to transform student with shared group info
const transformSharedStudent = (student, sharedProfile, homeroomGroup, teacherName, selectedGrade) => {
    return {
        ...student,
        source: "shared",
        sourceProfile: sharedProfile,
        sharedGroupName: homeroomGroup.name?.replace(/^INTERNAL_/, "").replaceAll("_", " "),
        sharedGroupOwner: teacherName,
        homeroomTeacher: teacherName,
        homeroomGrade: sharedProfile.gradeName || selectedGrade
    };
};

/**
 * Hook to fetch all eligible students for differentiation setup
 * Includes students from homeroom profile and shared groups
 */
const useAllEligibleStudents = ({
    selectedGrade,
    selectedTemplate,
    gradingProfiles,
    allSharedGroupOptions,
    selectedSchoolLocationKey,
    schoolLevelTemplateDto,
    token,
    userDetails
}) => {
    const [sharedGradingProfileDtos, setSharedGradingProfileDtos] = useState([]);
    const [loading, setLoading] = useState(false);

    // Get shared grading profile IDs for the selected grade and school
    const relevantSharedProfileIds = useMemo(() => {
        if (!selectedGrade || !allSharedGroupOptions) return [];

        const filtered = allSharedGroupOptions.filter((group) => {
            const matchesGrade = group.sharedGradeName === selectedGrade;
            const matchesSchool =
                !selectedSchoolLocationKey || Number(group.sharedLocationKey) === Number(selectedSchoolLocationKey);
            return matchesGrade && matchesSchool;
        });

        return [...new Set(filtered.map((g) => g.sharedGradingProfile).filter(Boolean))];
    }, [selectedGrade, allSharedGroupOptions, selectedSchoolLocationKey]);

    // Fetch shared grading profile DTOs
    useEffect(() => {
        if (!token || !relevantSharedProfileIds.length) {
            setSharedGradingProfileDtos([]);
            return;
        }

        let alive = true;
        setLoading(true);

        Promise.all(
            relevantSharedProfileIds.map((id) =>
                ElementaryProgressReportDao({ action: "getGradingProfileById", gradingProfileId: id, token })
            )
        )
            .then((responses) => {
                if (!alive) return;
                setSharedGradingProfileDtos(responses.map((r) => r?.data?.payload).filter(Boolean));
            })
            .catch((error) => {
                console.error("Error fetching shared grading profiles:", error);
            })
            .finally(() => {
                if (alive) setLoading(false);
            });

        return () => {
            alive = false;
        };
    }, [relevantSharedProfileIds, token]);

    // Get all eligible students grouped by source
    const allEligibleStudents = useMemo(() => {
        if (!selectedGrade || !selectedTemplate) return { homeroom: [], shared: [], all: [] };
        // Helper function to get homeroom students
        const getHomeroomStudents = () => {
            const homeroomProfile = gradingProfiles?.find((p) => p.gradeName === selectedGrade);

            if (!homeroomProfile) return [];

            // Filter by school location if selected
            if (selectedSchoolLocationKey) {
                const homeroomSchool = schoolLevelTemplateDto?.firstSemester?.profileLocationKey;
                if (Number(homeroomSchool) !== Number(selectedSchoolLocationKey)) {
                    return [];
                }
            }

            const gradingCriteria = homeroomProfile.gradingCriteriaList?.find(
                (criteria) => Number.parseInt(criteria.parentTemplate, 10) === selectedTemplate.templateId
            );

            if (!gradingCriteria) return [];

            return (homeroomProfile.gradingProfileStudents || []).map((student) =>
                transformHomeroomStudent(student, homeroomProfile)
            );
        };

        // Helper function to get shared group students
        const getSharedGroupStudents = () => {
            return processAllSharedProfiles(
                sharedGradingProfileDtos,
                selectedTemplate,
                allSharedGroupOptions,
                selectedGrade,
                userDetails
            );
        };

        const homeroom = getHomeroomStudents();
        const shared = getSharedGroupStudents();
        const all = [...homeroom, ...shared];

        // Remove duplicates from 'all' based on student key
        const seenKeys = new Set();
        const uniqueAll = all.filter((student) => {
            if (seenKeys.has(student.key)) return false;
            seenKeys.add(student.key);
            return true;
        });

        return { homeroom, shared, all: uniqueAll };
    }, [
        selectedGrade,
        selectedTemplate,
        gradingProfiles,
        sharedGradingProfileDtos,
        allSharedGroupOptions,
        userDetails,
        selectedSchoolLocationKey,
        schoolLevelTemplateDto
    ]);

    return {
        allEligibleStudents,
        sharedGradingProfileDtos,
        loading
    };
};

export default useAllEligibleStudents;
